import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Loader2, Search, Keyboard } from 'lucide-react';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { useGoogleMaps } from '../providers/GoogleMapsProvider';

interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  lat?: number;
  lng?: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: ParsedAddress) => void;
  placeholder?: string;
  className?: string;
}

interface PredictionItem {
  placeId: string;
  mainText: string;
  secondaryText: string;
  toPlace: () => google.maps.places.Place;
}

function parseAddressComponents(
  components: google.maps.places.AddressComponent[]
): ParsedAddress {
  let streetNumber = '';
  let route = '';
  let city = '';
  let state = '';
  let zipCode = '';

  for (const comp of components) {
    const types = comp.types;
    if (types.includes('street_number')) {
      streetNumber = comp.longText || '';
    } else if (types.includes('route')) {
      route = comp.longText || '';
    } else if (types.includes('locality')) {
      city = comp.longText || '';
    } else if (types.includes('sublocality_level_1') && !city) {
      city = comp.longText || '';
    } else if (types.includes('administrative_area_level_1')) {
      state = comp.shortText || '';
    } else if (types.includes('postal_code')) {
      zipCode = comp.longText || '';
    }
  }

  const street = [streetNumber, route].filter(Boolean).join(' ');
  return { street, city, state, zipCode };
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  className,
}: AddressAutocompleteProps) {
  const { isLoaded } = useGoogleMaps();
  const [predictions, setPredictions] = useState<PredictionItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [manualMode, setManualMode] = useState(false);
  const [apiReady, setApiReady] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;

    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    setApiReady(true);
  }, [isLoaded]);

  const fetchPredictions = useCallback(
    async (query: string) => {
      if (query.length < 3 || !apiReady || manualMode) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const request: google.maps.places.AutocompleteRequest = {
          input: query,
          includedRegionCodes: ['us'],
          language: 'en-US',
          sessionToken: sessionTokenRef.current!,
        };

        const { suggestions } =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const items: PredictionItem[] = suggestions
          .filter((s) => s.placePrediction)
          .map((s) => ({
            placeId: s.placePrediction!.placeId,
            mainText: s.placePrediction!.mainText?.text || '',
            secondaryText: s.placePrediction!.secondaryText?.text || '',
            toPlace: () => s.placePrediction!.toPlace(),
          }));

        setIsLoading(false);
        setPredictions(items);
        setIsOpen(items.length > 0);
        setActiveIndex(-1);
      } catch {
        setIsLoading(false);
        setPredictions([]);
        setIsOpen(false);
      }
    },
    [manualMode, apiReady]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);

    if (manualMode) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  }

  async function handleSelect(prediction: PredictionItem) {
    setIsLoading(true);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      });

      const components = place.addressComponents;
      if (components) {
        const parsed = parseAddressComponents(components);
        // Capture geo coordinates so downstream delivery (Uber Direct) can
        // pin the exact dropoff rather than re-geocoding the text address.
        const loc = place.location;
        if (loc) {
          parsed.lat = typeof loc.lat === 'function' ? loc.lat() : (loc.lat as unknown as number);
          parsed.lng = typeof loc.lng === 'function' ? loc.lng() : (loc.lng as unknown as number);
        }
        onChange(parsed.street);
        onSelect(parsed);
      }
    } catch {
      // Selection failed silently
    }

    setIsLoading(false);
    setIsOpen(false);
    setPredictions([]);
    inputRef.current?.blur();

    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || predictions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < predictions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : predictions.length - 1
      );
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(predictions[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }

  function toggleManualMode() {
    setManualMode((prev) => !prev);
    setPredictions([]);
    setIsOpen(false);
  }

  const googleAvailable = isLoaded && apiReady;

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0 && !manualMode) setIsOpen(true);
          }}
          placeholder={
            manualMode
              ? 'Type your address manually...'
              : googleAvailable
                ? placeholder
                : 'Type your address...'
          }
          className="pl-9"
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>

      {isOpen && predictions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          {predictions.map((p, i) => (
            <button
              key={p.placeId}
              type="button"
              className={cn(
                'flex items-start gap-2.5 w-full px-3 py-2.5 text-left transition-colors',
                i === activeIndex ? 'bg-purple-50' : 'hover:bg-gray-50'
              )}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => handleSelect(p)}
            >
              <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-heading truncate">
                  {p.mainText}
                </p>
                {p.secondaryText && (
                  <p className="text-xs text-body truncate">
                    {p.secondaryText}
                  </p>
                )}
              </div>
            </button>
          ))}
          <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <p className="text-[10px] text-gray-400">Powered by Google</p>
            <button
              type="button"
              onClick={toggleManualMode}
              className="text-[10px] text-primary hover:text-primary/80 font-medium flex items-center gap-1"
            >
              <Keyboard className="h-3 w-3" />
              Enter manually
            </button>
          </div>
        </div>
      )}

      {googleAvailable && !isOpen && (
        <div className="flex items-center justify-end mt-1">
          <button
            type="button"
            onClick={toggleManualMode}
            className="text-[11px] text-primary/70 hover:text-primary font-medium flex items-center gap-1 transition-colors"
          >
            {manualMode ? (
              <>
                <Search className="h-3 w-3" />
                Use address search
              </>
            ) : (
              <>
                <Keyboard className="h-3 w-3" />
                Can't find your address? Enter manually
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
